import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MenuItem, Select, Box, Typography, CircularProgress } from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import * as CountryFlags from "country-flag-icons/react/3x2";
import { useGetAllPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { setSelectedPlatform } from "state/slices/platformSlice";

// Renders the flag matching the platform's API-provided `flag_code`
// (e.g. "US", "EU", "GB"). Unknown or missing codes fall back to a globe icon,
// so adding a new platform never requires a frontend change.
const PlatformIcon = ({ flagCode }) => {
  const Flag = flagCode ? CountryFlags[String(flagCode).toUpperCase()] : null;

  if (!Flag) {
    return <PublicIcon sx={{ fontSize: 20, color: "#008080" }} />;
  }

  return <Flag style={{ width: 24, height: 16, borderRadius: "2px" }} />;
};

const PlatformSelector = ({ disabled = false }) => {
  const dispatch = useDispatch();
  // Only fetch platforms explicitly marked as visible by the API (`visible=true`).
  const { data: platforms, isLoading } = useGetAllPlatformsQuery(true);
  const selectedPlatform = useSelector((state) => state.platform);

  const filteredPlatforms = platforms || [];

  // Find deloitte-global as default
  const defaultPlatform = filteredPlatforms?.find(p => p.name === "deloitte-global");

  // Set default platform when platforms load
  useEffect(() => {
    // If we already have a saved selection in localStorage prefer that,
    // otherwise fall back to the `deloitte-global` default.
    if (filteredPlatforms.length > 0 && !selectedPlatform.selectedPlatformId) {
      const saved = localStorage.getItem("selectedPlatform");
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const savedPlatform = filteredPlatforms.find((p) => p._id === parsed.platformId);
          if (savedPlatform) {
            dispatch(
              setSelectedPlatform({
                platformId: savedPlatform._id,
                platformName: savedPlatform.display_name,
              })
            );
            return;
          }
        } catch (e) {
          // ignore JSON parse errors and fall back to default
        }
      }

      if (defaultPlatform) {
        dispatch(
          setSelectedPlatform({
            platformId: defaultPlatform._id,
            platformName: defaultPlatform.display_name,
          })
        );
        // Persist the default selection so refresh keeps it
        try {
          localStorage.setItem(
            "selectedPlatform",
            JSON.stringify({
              platformId: defaultPlatform._id,
              platformName: defaultPlatform.display_name,
            })
          );
        } catch (e) {
          // ignore storage errors
        }
      }
    }
  }, [filteredPlatforms, defaultPlatform, selectedPlatform.selectedPlatformId, dispatch]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography variant="body2" sx={{ color: "#666", whiteSpace: "nowrap", fontWeight: "500" }}>
        Platform
      </Typography>
      <Select
        value={selectedPlatform.selectedPlatformId || defaultPlatform?._id || ""}
        onChange={(e) => {
          const platformId = e.target.value;
          const platform = filteredPlatforms?.find(p => p._id === platformId);
          if (platform) {
            console.log("Platform changed to:", platform.name, platform._id);
              dispatch(setSelectedPlatform({
                platformId: platform._id,
                platformName: platform.display_name,
              }));
              try {
                localStorage.setItem(
                  "selectedPlatform",
                  JSON.stringify({
                    platformId: platform._id,
                    platformName: platform.display_name,
                  })
                );
              } catch (e) {
                // ignore storage errors
              }
          }
        }}
        renderValue={(value) => {
          const platform = filteredPlatforms?.find(p => p._id === value);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <PlatformIcon flagCode={platform?.flag_code} />
              <span>{platform?.display_name || "Select Platform"}</span>
            </Box>
          );
        }}
        displayEmpty
        size="small"
        disabled={disabled || isLoading}
        sx={{
          minWidth: 200,
          height: "31px",
          "& .MuiSelect-select": {
            height: "31px",
            display: "flex",
            alignItems: "center",
            padding: "0 14px",
          },
        }}
      >
        {isLoading ? (
          <MenuItem value="" disabled>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            Loading platforms...
          </MenuItem>
        ) : filteredPlatforms && filteredPlatforms.length > 0 ? (
          filteredPlatforms.map((platform) => {
            return (
              <MenuItem key={platform._id} value={platform._id}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <PlatformIcon flagCode={platform.flag_code} />
                  <span>{platform.display_name}</span>
                </Box>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem value="" disabled>
            No platforms available
          </MenuItem>
        )}
      </Select>
    </Box>
  );
};

export default PlatformSelector;
